/* Shared native review dialog. Judge.me owns the original form and submission. */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  // Presentation only: never clone, reorder or replace Judge.me's nodes/actions.
  function syncSummary(root) {
    const widget = root.querySelector('.jdgm-rev-widg');
    if (!widget) return null;
    const countAttribute = widget.getAttribute('data-number-of-reviews');
    const averageAttribute = widget.getAttribute('data-average-rating');
    const count = countAttribute === null || countAttribute.trim() === '' ? NaN : Number(countAttribute);
    const average = averageAttribute === null || averageAttribute.trim() === '' ? NaN : Number(averageAttribute);
    const hasCount = Number.isInteger(count) && count >= 0;
    const hasAverage = Number.isFinite(average) && average > 0 && average <= 5;
    const aggregate = hasCount && count === 0 ? 'empty' : hasCount && hasAverage ? 'ready' : 'unknown';
    if (widget.dataset.jdgmAggregate !== aggregate) widget.dataset.jdgmAggregate = aggregate;

    const classes = {
      '.jdgm-rev-widg': 'ui-reviews__layout',
      '.jdgm-row-stars': 'ui-reviews__summary',
      '.jdgm-rev-widg__summary-average': 'ui-reviews__score',
      '.jdgm-rev-widg__summary-stars, .jdgm-rev__rating': 'ui-reviews__stars',
      '.jdgm-histogram': 'ui-reviews__histogram',
      '.jdgm-histogram__row:not(.jdgm-histogram__clear-filter)': 'ui-reviews__bar',
      '.jdgm-histogram__bar': 'ui-reviews__track',
      '.jdgm-histogram__bar-content': 'ui-reviews__fill',
      '.jdgm-row-actions': 'ui-reviews__toolbar',
      '.jdgm-sort-dropdown': 'ui-select',
      '.jdgm-rev-widg__reviews': 'ui-reviews__list',
      '.jdgm-rev': 'ui-review',
      '.jdgm-rev__header': 'ui-review__meta',
      '.jdgm-rev__content': 'ui-review__body',
      '.jdgm-rev__title': 'ui-review__title',
      '.jdgm-rev__buyer-badge': 'ui-label',
      '.jdgm-rev__pics, .jdgm-rev__vids': 'ui-review__media',
      '.jdgm-rev__reply': 'ui-review__reply',
      '.jdgm-paginate': 'ui-reviews__pagination',
      '.jdgm-paginate__page': 'ui-button ui-button--secondary',
    };
    Object.entries(classes).forEach(function ([selector, names]) {
      root.querySelectorAll(selector).forEach(function (node) {
        names.split(' ').forEach(function (name) {
          if (!node.classList.contains(name)) node.classList.add(name);
        });
      });
    });
    // Counts and bar widths belong to the provider. An absent histogram stays absent.
    widget.querySelectorAll('.jdgm-histogram').forEach(function (histogram) {
      const frequencies = Array.from(histogram.querySelectorAll('.jdgm-histogram__frequency'));
      const hasCounts = frequencies.length === 5 && frequencies.every(function (frequency) {
        return /^\(?\d+\)?$/.test(frequency.textContent.trim());
      });
      histogram.hidden = !hasCounts || aggregate !== 'ready';
    });
    widget.querySelectorAll('.jdgm-paginate__page').forEach(function (page) {
      const current = page.classList.contains('jdgm-curt');
      if (current && page.getAttribute('aria-current') !== 'page') page.setAttribute('aria-current', 'page');
      if (!current && page.hasAttribute('aria-current')) page.removeAttribute('aria-current');
    });
    // The aggregate score is never inferred from the reviews on the current page.
    if (aggregate === 'ready') {
      const score = widget.querySelector('.jdgm-rev-widg__summary-average');
      const formatted = new Intl.NumberFormat(document.documentElement.lang || 'nl', {
        maximumFractionDigits: 2,
      }).format(average);
      if (score && score.textContent !== formatted) score.textContent = formatted;
    }
    const hasReviews = Boolean(widget.querySelector('.jdgm-rev'));
    if (aggregate === 'empty' && !hasReviews) return 'empty';
    return hasReviews ? 'ready' : null;
  }

  function initReviews() {
    const root = document.querySelector('.product-reviews-judgeme');
    const provider = root && root.querySelector('#judgeme_product_reviews');
    if (!provider) return;
    const status = root.querySelector('[data-jdgm-status]');
    let timeout;
    let state = 'loading';

    function showState(next) {
      if (state === next) return;
      state = next;
      ['loading', 'empty', 'error'].forEach(function (name) {
        root.querySelector('[data-jdgm-' + name + ']').hidden = name !== next;
      });
      status.textContent = next === 'error' ? status.dataset.errorText : next === 'empty' ? status.dataset.emptyText : '';
      if (next === 'ready' || next === 'empty') clearTimeout(timeout);
    }

    function refresh() {
      const next = syncSummary(root);
      if (next) showState(next);
    }

    // Attribute changes cover late aggregates and verification; child changes cover
    // async hydration, translated/original text, sorting and every provider page.
    const observer = new MutationObserver(refresh);
    observer.observe(provider, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-average-rating', 'data-number-of-reviews', 'data-verified-buyer', 'class'],
    });
    timeout = setTimeout(function () {
      if (state === 'loading') showState('error');
    }, 15000);
    root.querySelector('[data-jdgm-retry]').addEventListener('click', function () {
      window.location.reload();
    });
    refresh();
    document.addEventListener('shopify:section:unload', function (event) {
      if (event.target.contains(root)) {
        observer.disconnect();
        clearTimeout(timeout);
      }
    });
  }

  function initModal() {
    const modal = document.getElementById('jdgm-modal');
    const root = modal && modal.closest('.product-reviews-judgeme');
    const provider = root && root.querySelector('#judgeme_product_reviews');
    if (!provider) return;
    const formHost = modal.querySelector('#jdgm-modal-form-host');
    const loading = modal.querySelector('[data-jdgm-form-loading]');
    const error = modal.querySelector('[data-jdgm-form-error]');
    const status = modal.querySelector('[data-jdgm-form-status]');
    let session = null;
    let opener = null;

    function showState(state) {
      loading.hidden = state !== 'loading';
      error.hidden = state !== 'error';
      status.textContent = state === 'loading' ? status.dataset.loadingText : state === 'error' ? status.dataset.errorText : '';
    }

    function findForm() {
      const selectors = '.jdgm-form-wrapper, .jdgm-rev-widg__form-wrapper, .jdgm-rev-widg__form, .jdgm-form';
      // Modern Judge.me can render outside the product widget; never take an
      // unrelated store/product form or move the provider's overlay shell.
      const external = document.getElementById('jdgm-rev-widg-form-modal-wrap');
      const form = provider.querySelector(selectors) || (external && external.querySelector(selectors));
      return form && form.querySelector('input:not([type="hidden"]), textarea, select, button[type="submit"]') ? form : null;
    }

    function decorateForm() {
      const classes = {
        '.jdgm-form__fieldset': 'ui-form-field',
        'label': 'ui-form-field__label',
        'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="submit"]):not([type="button"]), textarea, select': 'ui-form-field__control',
        'select': 'ui-select',
        '.jdgm-form__rating': 'ui-reviews__stars',
        'button[type="submit"], input[type="submit"], .jdgm-submit-rev, .jdgm-form__submit': 'ui-button ui-button--primary',
        '.jdgm-cancel-rev, .jdgm-form__cancel, .jdgm-form__cancel-link': 'ui-button ui-button--secondary',
        '.jdgm-form__error, label.error': 'ui-form-field__error',
      };
      Object.entries(classes).forEach(function ([selector, names]) {
        formHost.querySelectorAll(selector).forEach(function (node) {
          names.split(' ').forEach(function (name) { node.classList.add(name); });
        });
      });
    }

    function stopWaiting(current) {
      clearTimeout(current.timeout);
      if (current.observer) current.observer.disconnect();
    }

    function attachForm(current) {
      if (session !== current || !modal.open) return false;
      const form = findForm();
      if (!form) return false;
      stopWaiting(current);
      current.form = form;
      current.parent = form.parentNode;
      current.sibling = form.nextSibling;
      current.style = form.getAttribute('style');
      current.hadClass = form.classList.contains('ui-reviews__form');
      form.classList.add('ui-reviews__form');
      formHost.appendChild(form);
      // Inline !important can outrank the shared form class. Reveal only this
      // wrapper while hosted; restore the exact original style attribute.
      if (getComputedStyle(form).display === 'none') form.style.setProperty('display', 'block', 'important');
      decorateForm();
      const focusWasOnRetry = document.activeElement === modal.querySelector('[data-jdgm-form-retry]');
      showState('ready');
      // Focus the form when it first becomes available, including recovery
      // from retry. Native dialog keeps subsequent keyboard focus inside.
      if (focusWasOnRetry || document.activeElement === modal || document.activeElement === modal.querySelector('[data-ui-dialog-close]')) {
        const field = form.querySelector('input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(:disabled), a[href]');
        if (field) field.focus();
      }
      // Decorate later provider controls without changing submission handlers.
      current.observer = new MutationObserver(decorateForm);
      current.observer.observe(form, { childList: true, subtree: true });
      return true;
    }

    function restoreForm() {
      const current = session;
      if (!current) return;
      session = null;
      stopWaiting(current);
      if (!current.form) return;
      const form = current.form;
      if (current.sibling && current.sibling.parentNode === current.parent) {
        current.parent.insertBefore(form, current.sibling);
      } else {
        current.parent.appendChild(form);
      }
      if (!current.hadClass) form.classList.remove('ui-reviews__form');
      if (current.style === null) form.removeAttribute('style');
      else form.setAttribute('style', current.style);
    }

    function loadForm() {
      if (!modal.open || session) return;
      const current = {};
      session = current;
      showState('loading');
      // Reuse the original form before asking Judge.me to open. Modern widgets
      // otherwise open a second modal over the page when this form already exists.
      if (attachForm(current)) return;
      // Observe before asking the provider to open, including synchronous
      // injection. Disconnect on every close, timeout and successful attach.
      current.observer = new MutationObserver(function () { attachForm(current); });
      current.observer.observe(document.body, { childList: true, subtree: true });
      current.timeout = setTimeout(function () {
        if (session !== current || !modal.open) return;
        if (attachForm(current)) return;
        stopWaiting(current);
        showState('error');
      }, 15000);
      const link = provider.querySelector('.jdgm-write-rev-link');
      if (link) link.click();
      attachForm(current);
    }

    root.querySelectorAll('[data-jdgm-modal-open]').forEach(function (button) {
      button.addEventListener('click', function () {
        // app.js already called showModal() during capture. Calling it again
        // would race with native focus restoration and provider hydration.
        if (!modal.open || session) return;
        opener = button;
        loadForm();
      });
    });
    modal.querySelector('[data-jdgm-form-retry]').addEventListener('click', function () {
      restoreForm();
      loadForm();
    });
    function onClose() {
      if (modal.open) return;
      restoreForm();
      showState('idle');
      if (opener && opener.isConnected) opener.focus();
    }
    modal.addEventListener('close', onClose);
    // Shared close controls call close() on capture; clean up synchronously
    // so a quick reopen cannot inherit a previous loading attempt.
    modal.addEventListener('click', function (event) {
      if (event.target.closest('[data-ui-dialog-close]')) onClose();
      if (event.target.closest('.jdgm-cancel-rev, .jdgm-form__cancel, .jdgm-form__cancel-link')) {
        modal.close();
        onClose();
      }
    });
    modal.addEventListener('beforetoggle', function (event) {
      // Covers Escape and programmatic close before a possible immediate reopen.
      if (event.newState === 'closed') restoreForm();
    });
    document.addEventListener('shopify:section:unload', function (event) {
      if (!event.target.contains(root)) return;
      restoreForm();
      if (modal.open) modal.close();
    });
  }

  ready(function () {
    initModal();
    initReviews();
  });
})();
